import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { catchError, of } from 'rxjs';
import { BusinessAccount } from '@/app/common/general-types/business-account.dto';
import { BusinessAccountService } from '@/app/services/business-account/business-account.service';
import { ImageService } from '@/app/services/image/image.service';
import { LoadingInlineComponent } from '@/app/atoms/loading-inline/loading-inline.component';
import { formatBriefTrackerDate } from '../../settings/molecules/brief-tracker/brief-tracker.helpers';

@Component({
  selector: 'admin-business-profile',
  imports: [LoadingInlineComponent],
  templateUrl: './business-profile.html',
  host: { class: 'block h-full w-full min-h-0' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BusinessProfile implements OnInit {
  readonly business = signal<BusinessAccount | undefined>(undefined);
  readonly loadingState = signal<'idle' | 'loading' | 'loaded' | 'error'>('idle');
  readonly businessIdCopied = signal(false);

  readonly copyIdButtonClass = computed(() =>
    this.businessIdCopied()
      ? 'border-green-500/45 text-green-500'
      : 'border-zinc-400/35 dark:border-zinc-500/45',
  );

  readonly copyIdAriaLabel = computed(() =>
    this.businessIdCopied() ? 'Copied business ID' : 'Copy business ID',
  );

  readonly imageService = inject(ImageService);
  private route = inject(ActivatedRoute);
  private destroyRef = inject(DestroyRef);
  private businessAccountService = inject(BusinessAccountService);

  ngOnInit(): void {
    const businessAccountId = this.route.snapshot.paramMap.get('businessAccountId');
    if (businessAccountId) {
      this.loadBusiness(businessAccountId);
    } else {
      this.loadingState.set('error');
    }
  }

  formatDate(value: Date | string | undefined): string {
    if (!value) {
      return '—';
    }
    return formatBriefTrackerDate(value);
  }

  copyBusinessId(id: string): void {
    void navigator.clipboard.writeText(id).then(() => {
      this.businessIdCopied.set(true);
      window.setTimeout(() => this.businessIdCopied.set(false), 2000);
    });
  }

  private loadBusiness(businessAccountId: string): void {
    this.loadingState.set('loading');
    this.businessAccountService
      .getBusinessAccountById(businessAccountId)
      .pipe(
        catchError(() => {
          this.loadingState.set('error');
          return of(undefined);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((business) => {
        if (business) {
          this.business.set(business);
          this.loadingState.set('loaded');
        }
      });
  }
}
